import { useState } from 'react';
import { useAuthStore } from '../stores/auth.store';
import { authApi } from '../services/auth.service';
import { tenantConfig } from '../lib/tenant';

type AuthView = 'login' | 'register' | 'forgot';

export default function AuthPage() {
  const [view, setView] = useState<AuthView>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const { setAuth } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (view === 'forgot') {
        await authApi.forgotPassword(email);
        setSuccess('Se o email estiver cadastrado, você receberá as instruções em breve.');
        return;
      }

      let response;
      if (view === 'login') {
        response = await authApi.login({ email, password });
      } else {
        response = await authApi.register({ email, password, name, workspaceName });
      }

      setAuth(response.token, response.user);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao autenticar');
    } finally {
      setLoading(false);
    }
  };

  const switchView = (next: AuthView) => {
    setView(next);
    setError('');
    setSuccess('');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card text-card-foreground rounded-lg shadow-lg border border-border">
        <div className="text-center p-6 border-b border-border">
          <div className="flex justify-center mb-3">
            {tenantConfig.logoUrl ? (
              <img src={tenantConfig.logoUrl} alt={tenantConfig.appName} className="h-12" />
            ) : (
              <span className="text-2xl font-bold text-foreground">{tenantConfig.appName}</span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {view === 'login' && 'Entre na sua conta'}
            {view === 'register' && 'Crie sua conta'}
            {view === 'forgot' && 'Recuperar senha'}
          </p>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-destructive/10 text-destructive border border-destructive/30 px-4 py-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 text-green-700 border border-green-200 px-4 py-3 rounded-lg mb-4 text-sm">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {view === 'register' && (
              <>
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm font-medium text-foreground block">
                    Nome
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Seu nome"
                    className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="workspace" className="text-sm font-medium text-foreground block">
                    Nome do Workspace
                  </label>
                  <input
                    id="workspace"
                    type="text"
                    value={workspaceName}
                    onChange={(e) => setWorkspaceName(e.target.value)}
                    placeholder="Minha Empresa"
                    required
                    className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-foreground block">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
            </div>

            {view !== 'forgot' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="text-sm font-medium text-foreground block">
                    Senha
                  </label>
                  {view === 'login' && (
                    <button
                      type="button"
                      onClick={() => switchView('forgot')}
                      className="text-xs text-muted-foreground hover:text-foreground underline"
                    >
                      Esqueci minha senha
                    </button>
                  )}
                </div>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full px-3 py-2 bg-background text-foreground border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (view === 'forgot' && !!success)}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? 'Carregando...'
                : view === 'login'
                ? 'Entrar'
                : view === 'register'
                ? 'Criar Conta'
                : 'Enviar instruções'}
            </button>
          </form>

          <div className="mt-6 text-center space-y-2">
            {view === 'login' && (
              <button type="button" onClick={() => switchView('register')} className="text-sm text-muted-foreground hover:text-foreground underline block w-full">
                Não tem conta? Cadastre-se
              </button>
            )}
            {view === 'register' && (
              <button type="button" onClick={() => switchView('login')} className="text-sm text-muted-foreground hover:text-foreground underline block w-full">
                Já tem conta? Faça login
              </button>
            )}
            {view === 'forgot' && (
              <button type="button" onClick={() => switchView('login')} className="text-sm text-muted-foreground hover:text-foreground underline block w-full">
                Voltar para o login
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
